
import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2 } from 'react-feather';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";

export default function ViewRoles() {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const prevSearch = useRef(searchTerm);

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        const delay = isSearchChanged ? 500 : 0;
        prevSearch.current = searchTerm;

        const timer = setTimeout(() => {
            fetchData(searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [searchTerm, sortColumn, sortDirection]);

    const fetchData = async (search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                search: search
            };
            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("roles/getall", body);
            // Filter out Admin role from the list if not handled by backend
            const filteredRoles = Array.isArray(response)
                ? response.filter((role: any) => role.name !== 'Admin')
                : response;

            // If response is paginated (data key), handle it, but for now assuming list return based on backend update
            if (response && response.data && Array.isArray(response.data)) {
                // Handle pagination case if you switched to it
                setData(response.data.filter((role: any) => role.name !== 'Admin'));
            } else {
                setData(filteredRoles);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_role`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_role`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("roles/delete", { id: id });
                    toast.success("Role deleted successfully", { autoClose: 1000 });
                    fetchData(searchTerm, sortColumn, sortDirection);
                } catch (error) {
                    toast.error("Failed to delete role", { autoClose: 1000 });
                }
            }
        });
    };

    const columns = [
        {
            name: "Sl.No",
            selector: (row: any, index?: number) => (index ?? 0) + 1,
            sortable: false,
            // width: "150px",
        },
        {
            name: "Role Name",
            selector: (row: any) => row.name,
            sortable: true,
            sortField: "name",
        },
        {
            name: "Actions",
            cell: (row: any) => (
                <div className="d-flex justify-content-between gap-2">
                    <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                        <Edit size={14} />
                    </Button>
                    {hasPermission('role-management.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Role Management" parent="Master Settings" title="Role Management" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Roles Overview</H3>
                                {hasPermission('role-management.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Role
                                    </Btn>
                                )}
                            </Col>
                            {hasPermission('role-management.view') && (
                                <>
                                    <Col xs={12} className="mb-3 d-flex justify-content-end">
                                        <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                    </Col>
                                    <div className="table-responsive">
                                        <DataTable
                                            data={data}
                                            columns={columns}
                                            pagination
                                            progressPending={loading}
                                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                            highlightOnHover
                                            sortServer
                                            onSort={handleSort}
                                            customStyles={customStyles}
                                        />
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Container>
            <ToastContainer />
        </div>
    );
}
